// src/contexts/TemplateContext.js
import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import useCachedData from '../hooks/useCachedData';
import { isPermissionDenied } from '../utils/firestoreErrors';
import { useAuth } from './AuthContext';
import { sortByName } from '../utils/sortByName';

const TemplateContext = createContext();

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Template context provider for efficient template caching and lookup
 * Uses the existing caching system for optimal performance
 */
export function TemplateProvider({ children }) {
  const { subscriberId, claimsReady, tokenClaims } = useAuth();

  // The workspace to read templates for, or null while the read would be
  // refused. This provider is mounted at the root, above login, so it is
  // already listening when a workspace is chosen. `subscriberId` is set at
  // that moment, but the ID token only carries the workspace once
  // set-custom-claims has answered and the token has been refreshed, and the
  // rules check the token. Keyed on `subscriberId` alone, the read went out
  // in that gap, was refused, and nothing read it again until a full reload:
  // the task filter said "Filter 0 templates… No templates match." in a
  // workspace with dozens. So the read waits for the token.
  //
  // A customer-portal token can never read form templates
  // (`hasSubscriberAccess` excludes role customer), so it does not try.
  const readableSubscriberId =
    subscriberId && claimsReady === true && tokenClaims?.role !== 'customer'
      ? subscriberId
      : null;

  // Fetch function for templates - consistent with TaskFilters.js implementation
  const fetchTemplates = useCallback(async () => {
    const subscriberId = readableSubscriberId;
    if (!subscriberId) {
      return [];
    }

    return new Promise((resolve, reject) => {
      // Deliberately no `where('deleted', '!=', true)`: a `!=` filter in
      // Firestore matches only documents where the field *exists*, so every
      // template written by the SSS migration — which did not set `deleted` —
      // was dropped from this query entirely. That is why a migrated
      // workspace's task filter read "Filter 0 templates… No templates
      // match." while the templates were plainly there. Deleted is a flag on
      // a few dozen documents, so filtering it client-side beside the
      // existing `active` filter costs nothing and cannot hide a document for
      // want of a field.
      const templatesQuery = query(
        collection(db, 'subscribers', subscriberId, 'formTemplates'),
        orderBy('template_name')
      );

      const unsubscribe = onSnapshot(
        templatesQuery,
        (snapshot) => {
          const temps = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(template => template.deleted !== true && template.active !== false)
            // This projection is an allowlist, not a formality: the result
            // is cached in localStorage, so it stays narrow on purpose and
            // leaves out the bulky parts of a template (translations above
            // all). The cost is that it fails silently — a consumer reading
            // a field nobody listed here gets `undefined` and renders
            // nothing, with no error to notice. `workflow_settings` was
            // missing until #692, which is why the auto-delete countdown
            // (#664) never appeared on a single card: every template looked
            // like one with retention switched off. Add the field here when
            // you start reading it, and cover it in TemplateContext.test.js.
            .map(template => ({
              id: template.id,
              name: template.template_name,
              description: template.description,
              version: template.version,
              // The default version's number. A task filled on another
              // version is rendered with that one (hooks/useTaskTemplate).
              version_no: template.version_no,
              fields: template.fields || [],
              image_categories: template.image_categories || [],
              color: template.template_color || null,
              // Retention (auto-delete), webhooks, customer sharing.
              workflow_settings: template.workflow_settings || null,
              // Whether "Edit task" is offered (utils/taskEditing.js).
              // Missing here, the switch read as off on every page.
              allow_task_editing: template.allow_task_editing === true,
              // What the review panel says a waiting task is waiting for.
              approval_required: template.approval_required === true,
            }));
          resolve(sortByName(temps));
          unsubscribe();
        },
        (error) => {
          // useCachedData reports it, at debug level while it retries a refusal.
          if (!isPermissionDenied(error)) {
            console.error('TemplateContext: Error loading templates:', error);
          }
          reject(error);
          unsubscribe();
        }
      );
    });
  }, [readableSubscriberId]);

  // Use cached data for templates with 5 minute TTL. A refused read is
  // retried with backoff inside useCachedData, and never cached.
  const {
    data: templates,
    loading: templatesLoading,
    error: templatesError,
    refresh: refreshTemplates,
    addToCache: addTemplateToCache,
    updateInCache: updateTemplateInCache,
    removeFromCache: removeTemplateFromCache
  } = useCachedData('templates', fetchTemplates, {
    subscriberId: readableSubscriberId,
    ttl: 5 * 60 * 1000, // 5 minutes
    backgroundRefresh: true
  });

  // A new ID token is the other moment a refused read can succeed: the claims
  // may have changed under it (set in another tab, renewed by the SDK). Read
  // again then if the last read failed, or came back empty.
  const templatesErrorRef = useRef(templatesError);
  templatesErrorRef.current = templatesError;
  const templatesCountRef = useRef(templates.length);
  templatesCountRef.current = templates.length;
  const tokenIssuedAt = tokenClaims?.issuedAt ?? null;
  const lastTokenRef = useRef(tokenIssuedAt);
  useEffect(() => {
    if (lastTokenRef.current === tokenIssuedAt) return;
    lastTokenRef.current = tokenIssuedAt;
    if (!readableSubscriberId) return;
    if (templatesErrorRef.current || templatesCountRef.current === 0) {
      refreshTemplates().catch(() => {});
    }
    // Only a new token is the trigger; the rest is read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenIssuedAt]);

  /**
   * Read the templates again, in the background, if the list is empty.
   * For a picker that is about to show the list (the task filter popover):
   * an empty list there is almost always a failed read rather than a
   * workspace with no templates. The rows on screen are not cleared.
   */
  const refreshTemplatesIfEmpty = useCallback(() => {
    if (!readableSubscriberId) return Promise.resolve(undefined);
    if (Array.isArray(templates) && templates.length > 0) return Promise.resolve(templates);
    return refreshTemplates().catch(() => undefined);
  }, [readableSubscriberId, templates, refreshTemplates]);

  // O(1) lookup map, rebuilt only when templates change
  const templateMap = useMemo(() => {
    const map = new Map();
    templates.forEach(t => map.set(t.id, t));
    return map;
  }, [templates]);

  /**
   * Get template by ID with O(1) Map lookup
   */
  const getTemplateById = useCallback((templateId) => {
    if (!templateId) return null;
    return templateMap.get(templateId) || null;
  }, [templateMap]);

  /**
   * Get template name by ID with fallback
   */
  const getTemplateNameById = useCallback((templateId) => {
    if (!templateId) return null;
    return templateMap.get(templateId)?.name || null;
  }, [templateMap]);

  /**
   * Get template color by ID (returns null for invalid hex values)
   */
  const getTemplateColorById = useCallback((templateId) => {
    if (!templateId) return null;
    const color = templateMap.get(templateId)?.color;
    if (!color) return null;
    return HEX_COLOR_RE.test(color) ? color : null;
  }, [templateMap]);

  /**
   * Get templates for dropdown with "All" option
   */
  const getTemplatesForDropdown = useCallback(() => {
    // Always return at least the "All" option, and actual templates if loaded
    return [
      { id: 'all', name: 'All Templates' },
      // Sorted here as well: the cached list can predate the sorted read.
      ...sortByName(Array.isArray(templates) ? templates : [])
    ];
  }, [templates]);

  /**
   * Get template summary (name, field count, image categories)
   */
  const getTemplateSummary = useCallback((templateId) => {
    const template = getTemplateById(templateId);
    if (!template) return null;

    return {
      id: template.id,
      name: template.name,
      description: template.description,
      version: template.version,
      fieldCount: (template.fields || []).length,
      imageCategoryCount: (template.image_categories || []).length
    };
  }, [getTemplateById]);

  /**
   * Build template lookup map for bulk operations
   */
  const getTemplateLookupMap = useCallback(() => {
    const lookup = {};
    templates.forEach(template => {
      lookup[template.id] = {
        name: template.name,
        description: template.description,
        version: template.version,
        fieldCount: (template.fields || []).length,
        imageCategoryCount: (template.image_categories || []).length
      };
    });
    return lookup;
  }, [templates]);

  const contextValue = {
    // Data
    templates,
    templatesLoading,
    templatesError,

    // Core functions
    getTemplateById,
    getTemplateNameById,
    getTemplateColorById,
    getTemplatesForDropdown,
    getTemplateSummary,
    getTemplateLookupMap,

    // Cache management
    refreshTemplates,
    refreshTemplatesIfEmpty,
    addTemplateToCache,
    updateTemplateInCache,
    removeTemplateFromCache,

    // Utility
    hasTemplates: templates.length > 0
  };

  return (
    <TemplateContext.Provider value={contextValue}>
      {children}
    </TemplateContext.Provider>
  );
}

/**
 * Hook to use template context
 */
export function useTemplates() {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
}

/**
 * Higher-order component to provide template context
 */
export function withTemplates(Component) {
  return function WrappedComponent(props) {
    return (
      <TemplateProvider>
        <Component {...props} />
      </TemplateProvider>
    );
  };
}

export default TemplateContext;